<template>
  <SettingsLayout>
    <div class="settings-content">
      <div class="heading">
        <h1 class="h2">General</h1>
      </div>
      <div class="block">
        <div class="mxw">
          <h2 class="h3">Your Profile</h2>
          <div class="input-row">
            <h3>First Name</h3>
            <div class="form-input">
              <input
                type="text"
                aria-label="First name"
                v-model="user.firstName"
                placeholder="First Name"
                @blur="updateUser('firstName')"
              />
            </div>
          </div>
          <div class="input-row">
            <h3>Last Name</h3>
            <div class="form-input">
              <input
                type="text"
                aria-label="Last name"
                v-model="user.lastName"
                placeholder="Last Name"
                @blur="updateUser('lastName')"
              />
            </div>
          </div>
          <div class="input-row">
            <h3>Email</h3>
            <div class="form-input">
              <input
                id="email-address"
                type="text"
                aria-label="Email"
                v-model="user.email"
                placeholder="Email"
                disabled
              />
            </div>
          </div>
        </div>
      </div>
      <div class="block">
        <div class="mxw">
          <h2 class="h3">Email Preferences</h2>
          <p>Determine how often you receive emails from us</p>
          <div class="radio-list">
            <ul>
              <li>
                <label>
                  <input
                    type="radio"
                    name="emailPreference"
                    value="NOTIFY"
                    v-model="user.emailPreference"
                    @change="updateUser('emailPreference')"
                  />
                  <span class="fake-radio"></span>
                  <span class="fake-label"
                    >Notify me whenever a file finishes processing</span
                  >
                </label>
              </li>
              <li>
                <label>
                  <input
                    type="radio"
                    name="emailPreference"
                    value="DO_NOT_NOTIFY"
                    v-model="user.emailPreference"
                    @change="updateUser('emailPreference')"
                  />
                  <span class="fake-radio"></span>
                  <span class="fake-label">Do not notify me</span>
                </label>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="block">
        <div class="mxw">
          <h2 class="h3">Transcription</h2>
          <div class="input-row">
            <h3>Default transcription language</h3>
            <div class="form-input">
              <select
                v-model="defaultLanguage"
                class="language-dropdown"
                aria-label="Default transcription language"
                :disabled="savingLanguage || loadingLanguage"
                @change="saveDefaultLanguage"
              >
                <option :value="autoDetect">Auto-detect</option>
                <option
                  v-for="option in languageOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
          <p class="notification-hint">
            <small>
              Used for new uploads and recordings, here and in the mobile apps.
              You can still pick a different language each time. Auto-detect
              works best if you record in more than one language.
            </small>
          </p>
        </div>
      </div>
      <div class="block" v-if="notificationStore.isSupported">
        <div class="mxw">
          <h2 class="h3">Browser Notifications</h2>
          <p>Get notified when your transcriptions are ready</p>
          <div class="notification-toggle">
            <div class="toggle-row">
              <label class="toggle-label">
                <span class="toggle-text">
                  {{
                    notificationStore.isEnabled
                      ? "Notifications enabled"
                      : "Notifications disabled"
                  }}
                </span>
                <div class="toggle-switch">
                  <input
                    type="checkbox"
                    :checked="notificationStore.isEnabled"
                    :disabled="
                      notificationStore.isActivating ||
                      notificationStore.isPermissionDenied
                    "
                    @change="toggleNotifications"
                  />
                  <span class="toggle-slider"></span>
                </div>
              </label>
            </div>
            <p
              v-if="notificationStore.isPermissionDenied"
              class="notification-hint"
            >
              <small>
                Notifications are blocked in your browser.
                <a href="#" @click.prevent="showBrowserSettingsHelp"
                  >Learn how to enable them</a
                >
              </small>
            </p>
            <p v-if="notificationStore.isActivating" class="notification-hint">
              <small>{{
                notificationStore.isEnabled ? "Disabling..." : "Enabling..."
              }}</small>
            </p>
          </div>
        </div>
      </div>
      <div class="block">
        <div class="mxw">
          <h2 class="h3">Delete Account</h2>
          <div class="watermark-toggle">
            <div class="btn-wrap">
              <a href="#" @click.prevent="showDeleteAccountPopup" class="btn"
                ><i class="icon-plus"></i>DELETE YOUR ACCOUNT</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </SettingsLayout>
</template>

<script>
import SettingsLayout from "@/components/SettingsLayout.vue";
import {
  getSettingsUser,
  updateSettingsUser,
} from "@/services/workspace.service";
import { useAppStore } from "@/stores/app-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useLanguagePreferenceStore } from "@/stores/language-preference-store";
import { mapStores } from "pinia";
import { analytics } from "@/services/analytics.service";
import {
  AUTO_DETECT,
  initialLanguage,
  languageOptions,
} from "@/constants/transcriptionLanguages";

export default {
  name: "SettingsProfileView",
  components: {
    SettingsLayout,
  },
  computed: {
    ...mapStores(useAppStore, useNotificationStore, useLanguagePreferenceStore),
    languageOptions() {
      return languageOptions(this.languagePreferenceStore.defaultLanguage);
    },
  },
  data() {
    return {
      user: {
        firstName: "",
        lastName: "",
        email: "",
        emailPreference: "",
      },
      defaultLanguage: AUTO_DETECT,
      // Locked until the saved default is known, so a quick change can't
      // race the first load.
      loadingLanguage: true,
      savingLanguage: false,
      autoDetect: AUTO_DETECT,
    };
  },
  mounted() {
    analytics.settingsProfileViewed();
    this.loadUserData();
    this.loadDefaultLanguage();
    // Initialize notification store
    this.notificationStore.initialize();
    this.notificationStore.checkPromptStatus();
  },
  methods: {
    async loadUserData() {
      try {
        const response = await getSettingsUser();
        console.log(response.data);
        this.user.firstName = response.data.firstName || ""; // Handle null case
        this.user.lastName = response.data.lastName || ""; // Handle null case
        this.user.email = response.data.email || "";
        // Fall back to the schema default (EmailPreference @default(NOTIFY)).
        // The old "NO_NOTIFY" fallback wasn't a valid value: no radio showed as
        // selected, and a name save would send the invalid enum to the server.
        this.user.emailPreference = response.data.emailPreference || "NOTIFY";
      } catch (error) {
        this.$toast.warning(`Failed to load user data.`);
      }
    },
    async updateUser(field) {
      try {
        // Track field edits
        if (field === "firstName") {
          analytics.profileFirstNameEdited();
        } else if (field === "lastName") {
          analytics.profileLastNameEdited();
        }

        // Send firstName, lastName, and emailPreference to update the user profile.
        // The API helper resolves HTTP failures as { error } — check it.
        const { error } =
          (await updateSettingsUser(
            this.user.firstName,
            this.user.lastName,
            this.user.emailPreference,
          )) || {};
        if (error) throw new Error(error.message || "Update failed");
        analytics.profileSaved();
        this.$toast.success(`Successfully updated ${field}`);
      } catch (error) {
        this.$toast.warning(`Failed to update ${field}`);
      }
    },
    async loadDefaultLanguage() {
      try {
        const saved = await this.languagePreferenceStore.load();
        this.defaultLanguage = initialLanguage(saved);
      } finally {
        // Usable even if the load failed: the user can still pick one.
        this.loadingLanguage = false;
      }
    },
    async saveDefaultLanguage() {
      const previous = initialLanguage(
        this.languagePreferenceStore.defaultLanguage,
      );
      this.savingLanguage = true;
      try {
        const saved = await this.languagePreferenceStore.save(
          this.defaultLanguage,
        );
        if (saved) {
          // Show what the server stored.
          this.defaultLanguage = initialLanguage(
            this.languagePreferenceStore.defaultLanguage,
          );
          this.$toast.success("Default transcription language updated");
        } else {
          this.defaultLanguage = previous;
          this.$toast.error(
            "Couldn't save your default language. Please try again.",
          );
        }
      } finally {
        this.savingLanguage = false;
      }
    },
    showDeleteAccountPopup() {
      console.log("Showing the Delete Account popup");
      analytics.profileDeleteAccountClicked();
      this.appStore.showDeleteAccountPopup = true;
    },
    async toggleNotifications(event) {
      await this.applyNotificationToggle();
      // The switch is bound one-way (:checked). A failed attempt leaves the
      // store unchanged, so Vue never re-renders it: put it back by hand.
      if (event?.target) {
        event.target.checked = this.notificationStore.isEnabled;
      }
    },
    async applyNotificationToggle() {
      // Use database userUUID (not Auth0 sub) to match backend Ably token
      const userUUID = this.appStore?.user?.userUUID;

      if (this.notificationStore.isEnabled) {
        // Disable notifications
        const success = await this.notificationStore.disableNotifications();
        if (success) {
          this.$toast.success("Browser notifications disabled");
        } else {
          this.$toast.error("Failed to disable notifications");
        }
      } else {
        // Enable notifications
        if (!userUUID) {
          this.$toast.error(
            "Unable to enable notifications. Please try again.",
          );
          return;
        }

        const success =
          await this.notificationStore.enableNotifications(userUUID);
        if (success) {
          this.$toast.success("Browser notifications enabled!");
        } else if (this.notificationStore.isPermissionDenied) {
          this.$toast.warning(
            "Notifications blocked. Please enable in browser settings.",
          );
        } else {
          this.$toast.error("Failed to enable notifications");
        }
      }
    },
    showBrowserSettingsHelp() {
      // Show help for enabling notifications in browser settings
      const message = `To enable notifications:
1. Click the lock/info icon in your browser's address bar
2. Find "Notifications" in the permissions
3. Change it from "Block" to "Allow"
4. Refresh this page`;
      alert(message);
    },
  },
};
</script>

<style scoped>
#email-address {
  background-color: lightgrey;
}

/* Notification Toggle Styles */
.notification-toggle {
  margin-top: 16px;
}

.toggle-row {
  display: flex;
  align-items: center;
}

.toggle-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
}

.toggle-text {
  font-size: 14px;
  color: var(--text-primary, #374151);
}

.toggle-switch {
  position: relative;
  width: 48px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: var(--primary-color, #6366f1);
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(24px);
}

.toggle-switch input:disabled + .toggle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

.notification-hint {
  margin-top: 8px;
  color: var(--text-muted, #9ca3af);
}

.notification-hint a {
  color: var(--primary-color, #6366f1);
  text-decoration: underline;
}
</style>
